import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { MaterialIcon } from '@dsny/dsny-component-library'
import { trackEvent } from 'utils/segmentService'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { StyledModal } from 'components/Modals/Modals.styles'
import {
  ActionButton,
  Actions,
  BodyText,
  CloseButton,
  CopyBlock,
  ModalContent,
  ModalHeader,
  Title
} from './PlayPlaylistModal.styles'

const PlayPlaylistModal = ({
  analyticsModalType = 'playlist_queue_replacement',
  hasError = false,
  isLoading = false,
  isOpen,
  onCancel,
  onConfirm,
  playlist = null
}) => {
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const titleKey = hasError
    ? 'playlist.playback-error-title'
    : 'playlist.replace-warning-title'
  const message = hasError
    ? t('playlist.playback-error-message')
    : t('playlist.replace-warning-message', { name: playlist?.name || '' })
  const cancelButton = (
    <ActionButton
      key="cancel"
      disabled={isLoading}
      mode="dark"
      setting="player"
      variant="secondary"
      onClick={onCancel}
    >
      {t('general.cancel')}
    </ActionButton>
  )
  const playButton = (
    <ActionButton
      key="play"
      disabled={isLoading}
      mode="dark"
      setting="player"
      variant="primary"
      onClick={onConfirm}
    >
      {isLoading ? t('general.loading') : t('general.play')}
    </ActionButton>
  )

  return (
    <StyledModal
      $isMobile={isMobile}
      analyticsModalType={analyticsModalType}
      bodyStyles={{ width: '100%' }}
      containerStyles={{
        boxSizing: 'border-box',
        maxWidth: 'calc(100vw - 32px)',
        overflow: 'hidden',
        padding: isMobile ? '24px 12px' : '32px',
        width: isMobile ? '288px' : '624px'
      }}
      isEscKeypressDisabled={isLoading}
      isMaskClickDisabled={isLoading}
      isOpen={isOpen}
      trackEvent={trackEvent}
      onClose={onCancel}
    >
      <ModalContent>
        <CopyBlock>
          <ModalHeader>
            <Title>{t(titleKey)}</Title>
            <CloseButton
              aria-label={t('general.cancel')}
              disabled={isLoading}
              type="button"
              onClick={onCancel}
            >
              <MaterialIcon name="X" color="currentColor" />
            </CloseButton>
          </ModalHeader>

          <BodyText $hasError={hasError} aria-live="polite">
            {message}
          </BodyText>
        </CopyBlock>

        <Actions>
          {isMobile
            ? [playButton, cancelButton]
            : [cancelButton, playButton]}
        </Actions>
      </ModalContent>
    </StyledModal>
  )
}

PlayPlaylistModal.propTypes = {
  analyticsModalType: PropTypes.string,
  hasError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  playlist: PropTypes.object
}

export default PlayPlaylistModal
